import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Colors, Dropdown, getIcon, IdentifierDTO, Options, useModal } from '@zazume/zzm-base';
import { FC, ReactNode } from 'react';
import { useAuth } from '#/contexts/AuthProvider';
import { useSigningDocument } from '#hooks/application/useSigningDocument';
import { useDownloadDocument } from '#hooks/documents/useDownloadDocument';
import { useI18n } from '#hooks/useI18n';
import { Document, UserRolesTypes } from '#/models';
import { isImage } from '#/utils/fileHelper';
import { SigningDocumentStateTag } from '../forms/signingDocumentPreview/SigningDocumentStateTag';
import { GenericDeleteConfirmationModal } from '../modal/GenericDeleteConfirmationModal';
import { Can } from '../roles/Can';
import { Body14 } from '../typography';
import { DocumentCardFootnote } from './DocumentCardFootnote';
import { DocumentModal } from './DocumentModal';
import { Tag } from '#/components/tag/Tag';

const Container = styled.div<any>(({ theme, thin }) => css`
  display: flex;
  position: relative;
  align-items: center;
  border: ${theme.borderWeights.light} solid ${theme.colors.Gray200};
  ${thin
    ? `padding: ${theme.spacing.s4} ${theme.spacing.s6} ${theme.spacing.s4} ${theme.spacing.s3};`
    : `padding: ${theme.spacing.s8} ${theme.spacing.s6} ${theme.spacing.s8} ${theme.spacing.s4};`}
  border-radius: ${theme.radius.lg};
  box-shadow: ${theme.shadows.s1};
  transition: .15s;
  width: 100%;

  svg {
    flex-shrink: 0;
  }

  :hover {
    cursor: pointer;
    background-color: ${theme.colors.Gray100};
  }
`);

const Details = styled.div`
  display: flex;
  flex-direction: column;
  margin-right: auto;
  margin-left: 4px;
  width: 100%;
`;

const FileName = styled(Body14)`
  color: ${Colors.FONT_3};
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-all;
`;

const TagsContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

interface DocumentCardProps {
  documentId: IdentifierDTO;
  relatedUser?: string;
  /**
   * Dispatched after confirming the deletion of the document
   * @param document
   */
  onRemove?: (document: Document) => void;
  onUpdate?: (document: Document) => void;
  deletePermissionRoles?: UserRolesTypes[];
  thin?: boolean;
  /**
   * Additional dropdown items to be rendered after the default items
   */
  moreDropdownItems?: ReactNode[];
  onNextClick?: (id: string) => void;
  onPreviousClick?: (id: string) => void;
  onOpenDocument?: (document: Document) => void;
}

export const DocumentCard: FC<DocumentCardProps> = ({ documentId, relatedUser, onRemove, onUpdate, deletePermissionRoles, thin, moreDropdownItems, onNextClick, onPreviousClick, onOpenDocument }) => {
  const { t } = useI18n();
  const { isOwner, user } = useAuth();

  const { RenderModal, open, close } = useModal({ modalSize: 'l' });
  const { RenderModal: RenderDeleteModal, open: openDeleteModal, close: closeDeleteModal } = useModal({ modalSize: 's', withMobileFullscreen: false });

  const { document, isLoading } = useSigningDocument(documentId);
  const { download } = useDownloadDocument(documentId);

  if (isLoading || !document) {
    return null;
  }

  const { filename, signatureExternalId, state, rejected, hasSignatureError, metadata } = document;

  const isManualUpload = metadata?.isManualUpload;
  const isSigningDocument = Boolean(signatureExternalId);
  const canRemoveSigningDocument = isSigningDocument ? Boolean(rejected) : true;
  const canUser = !isOwner || document.author === user?._id;

  const openDeleteModalHandler = (event: any) => {
    event.stopPropagation();
    openDeleteModal();
  };

  const isRemovable = Boolean(onRemove) && canRemoveSigningDocument && canUser;
  const removeHandler = async () => {
    if (onRemove) {
      await onRemove(document);
      closeDeleteModal();
    }
  };

  const isEditable = Boolean(onUpdate) && canUser;
  const editHandler = () => onUpdate && onUpdate(document);

  const handleOpenDocument = () => {
    if (onOpenDocument) {
      return onOpenDocument(document);
    }
    open();
  };

  const Icon = isImage(filename) ? getIcon('imageIcon') : getIcon('document');

  return (
    <>
      <Container thin={thin} onClick={handleOpenDocument}>
        <Icon color="Gray600" scale="big"/>
        <Details>
          <FileName>{filename}</FileName>
          <DocumentCardFootnote document={document} relatedUser={relatedUser}/>
        </Details>
        <TagsContainer>
          {isManualUpload && (<Tag backgroundColor={Colors.YELLOW_LIGHT} textColor={Colors.YELLOW_DARK} small>{t('documents.manuallyUploaded')}</Tag>)}
          {signatureExternalId && state && <SigningDocumentStateTag state={state} hasSignatureError={hasSignatureError}/>}
        </TagsContainer>
        <Dropdown header={<Options color="Gray600" size={20} cursor="pointer"/>} position="right" topValue={30}>
          <Dropdown.Item onClick={handleOpenDocument} icon="eye">
            {t('general.viewDocument')}
          </Dropdown.Item>
          <Dropdown.Item onClick={download} icon="download">
            {t('general.download')}
          </Dropdown.Item>
          {isEditable &&
            <Dropdown.Item onClick={editHandler} icon="pencil">
              {t('general.edit')}
            </Dropdown.Item>}
          {isRemovable &&
            <Can hasRoles={deletePermissionRoles || ['admin', 'buildingManager', 'ownerManager']}>
              <Dropdown.Item onClick={openDeleteModalHandler} icon="delete" variant="danger">
                {t('general.remove')}
              </Dropdown.Item>
            </Can>}
          {moreDropdownItems?.map((item, index) => (
            <div key={index}>
              {item}
            </div>
          ))}
        </Dropdown>
      </Container>

      {!onOpenDocument && (
        <RenderModal>
          <DocumentModal
            close={close}
            document={document}
            onNextClick={onNextClick}
            onPreviousClick={onPreviousClick}
          />
        </RenderModal>
      )}
      <RenderDeleteModal>
        <GenericDeleteConfirmationModal
          onConfirm={removeHandler}
          onCancel={closeDeleteModal}
          title={t('payments.detail.attachments.remove')}
          description={t('payments.detail.attachments.removeContent')}
        />
      </RenderDeleteModal>
    </>
  );
};
